import { useCallback, useState } from "react";
import { toast } from "sonner";
import { Check, FileSignature, Loader2 } from "lucide-react";
import SignaturePad from "./SignaturePad";

export const fmtAUD = (n) => new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }).format(Number(n || 0));
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : "");

function FeeTable({ items, total }) {
  return (
    <table className="w-full text-[13px]" data-testid="engagement-fee-table">
      <thead><tr className="text-[10px] uppercase tracking-wider text-[var(--ink-3)] border-b border-[var(--border)]">
        <th className="text-left py-2">Service</th><th className="text-right py-2">Fee (AUD)</th></tr></thead>
      <tbody>
        {(items || []).map((it, i) => (
          <tr key={i} className="border-b border-[var(--border)]">
            <td className="py-2">{it.name}</td><td className="py-2 text-right font-mono">{fmtAUD(it.effective_fee)}</td>
          </tr>
        ))}
        <tr><td className="py-2.5 font-medium">Total</td><td className="py-2.5 text-right font-mono font-medium" data-testid="engagement-total">{fmtAUD(total)}</td></tr>
      </tbody>
    </table>
  );
}

// v68.51 — shared letter + sign form for the emailed link and the portal.
export default function EngagementSignView({ eng, onSign }) {
  const [name, setName] = useState("");
  const [sig, setSig] = useState(null);
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const onSig = useCallback((v) => setSig(v), []);

  const submit = async (e) => {
    e.preventDefault();
    if (name.trim().length < 2 || !sig || !agree) { toast.error("Type your full name, draw your signature and tick the box."); return; }
    setBusy(true);
    try { await onSign({ typed_name: name.trim(), signature_data_url: sig }); toast.success("Signed — thank you."); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="engagement-sign-view">
      <div>
        <div className="overline mb-2 text-[var(--accent)]">{eng.firm_name}</div>
        <h1 className="font-heading text-3xl sm:text-4xl leading-tight" style={{ fontWeight: 500 }}>Engagement letter · {eng.financial_year}</h1>
        <div className="text-[13px] text-[var(--ink-3)] mt-1">Prepared for {eng.client_name}</div>
      </div>
      <div className="card p-6 whitespace-pre-wrap text-[14px] leading-relaxed" data-testid="engagement-letter-body">{eng.letter}</div>
      <div className="card p-6"><div className="overline mb-3">Fee schedule</div><FeeTable items={eng.items} total={eng.total} /></div>
      {eng.status === "signed" ? (
        <div className="card p-6 text-center" data-testid="engagement-signed">
          <Check size={28} className="mx-auto mb-2" style={{ color: "var(--moss)" }} />
          <div className="font-heading text-xl" style={{ fontWeight: 500 }}>Signed by {eng.signed_name}</div>
          <div className="text-[12px] text-[var(--ink-3)] mt-1">{fmtDate(eng.signed_at)} · valid until {fmtDate(eng.expires_at)}</div>
          {eng.signature_data_url && <img alt="signature" src={eng.signature_data_url} className="mt-4 mx-auto border border-[var(--border)] rounded bg-white" style={{ maxWidth: 300 }} />}
        </div>
      ) : eng.status !== "sent" ? (
        <div className="card p-6 text-center text-[var(--ink-3)]" data-testid="engagement-not-signable">This letter is {eng.status} and can no longer be signed.</div>
      ) : (
        <form onSubmit={submit} className="card p-6 space-y-4" data-testid="engagement-sign-form">
          <div>
            <label className="overline block mb-1.5">Type your full name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} data-testid="engagement-sign-name"
              className="w-full bg-white border border-[var(--border)] rounded-md px-3.5 py-2.5 text-[14px] focus:outline-none focus:border-[var(--ink)]" />
          </div>
          <SignaturePad onChange={onSig} testid="engagement-sign-canvas" />
          <label className="flex items-start gap-2 text-[12px] text-[var(--ink-2)]">
            <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-0.5" data-testid="engagement-sign-agree" />
            I have read this letter and agree to the terms and fees. My typed name and drawn signature are my legal signature; my IP address and time are recorded.
          </label>
          <button type="submit" disabled={busy} className="btn-primary w-full justify-center" data-testid="engagement-sign-submit">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <FileSignature size={14} />} {busy ? "Signing…" : "Sign engagement letter"}
          </button>
        </form>
      )}
    </div>
  );
}
